import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Building2, Upload, FileText, X, Sparkles, Network, Wifi, Server, Globe, ChevronDown } from "lucide-react";
import type { LandingPageConfig } from "@/types/applicant";
import NetworkBackground from "@/components/applicants/NetworkBackground";

const DRIVER_LICENSE_CLASSES = [
  { value: "AM", label: "AM" },
  { value: "A1", label: "A1" },
  { value: "A2", label: "A2" },
  { value: "A", label: "A" },
  { value: "B", label: "B" },
  { value: "BE", label: "BE" },
  { value: "B96", label: "B96" },
  { value: "C1", label: "C1" },
  { value: "C1E", label: "C1E" },
  { value: "C", label: "C" },
  { value: "CE", label: "CE" },
  { value: "D1", label: "D1" },
  { value: "D1E", label: "D1E" },
  { value: "D", label: "D" },
  { value: "DE", label: "DE" },
  { value: "L", label: "L" },
  { value: "T", label: "T" },
];

interface JobPosition {
  id: string;
  title: string;
  description: string | null;
}

interface UploadedFile {
  file: File;
  preview?: string;
}

const formSchema = z.object({
  first_name: z.string().min(2, "Vorname muss mindestens 2 Zeichen haben"),
  last_name: z.string().min(2, "Nachname muss mindestens 2 Zeichen haben"),
  email: z.string().email("Ungültige E-Mail-Adresse"),
  phone: z.string().min(6, "Telefonnummer muss mindestens 6 Zeichen haben"),
  address_street: z.string().optional(),
  address_zip: z.string().optional(),
  address_city: z.string().optional(),
  birth_date: z.string().optional(),
  desired_position: z.string().optional(),
  qualifications: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

const ApplyPage = () => {
  const [config, setConfig] = useState<LandingPageConfig | null>(null);
  const [jobPositions, setJobPositions] = useState<JobPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedLicenseClasses, setSelectedLicenseClasses] = useState<string[]>([]);
  const [licensePopoverOpen, setLicensePopoverOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const [companyInfo, setCompanyInfo] = useState<{
    name?: string;
    street?: string;
    houseNumber?: string;
    postalCode?: string;
    city?: string;
    logoBase64?: string;
  } | null>(null);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      address_street: "",
      address_zip: "",
      address_city: "",
      birth_date: "",
      desired_position: "",
      qualifications: "",
    },
  });

  useEffect(() => {
    document.title = "Flash HR Bewerbung";
    fetchData();
    loadCompanyInfo();
    return () => {
      document.title = "HR-flash Dashboard";
    };
  }, []);

  const fetchData = async () => {
    try {
      const [configRes, positionsRes] = await Promise.all([
        supabase
          .from("landing_page_config")
          .select("*")
          .eq("is_active", true)
          .single(),
        supabase
          .from("job_positions")
          .select("id, title, description")
          .eq("is_active", true)
          .order("sort_order", { ascending: true })
      ]);

      if (configRes.data) {
        setConfig(configRes.data as LandingPageConfig);
      }
      if (positionsRes.data) {
        setJobPositions(positionsRes.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyInfo = async () => {
    // First try localStorage (for logged-in users)
    const stored = localStorage.getItem("companySettings");
    if (stored) {
      setCompanyInfo(JSON.parse(stored));
      return;
    }
    // For non-authenticated users, fetch via secure RPC (limited fields only)
    try {
      const { data } = await supabase.rpc("get_public_company_info");
      if (data && typeof data === 'object') {
        const info = data as Record<string, string | null>;
        setCompanyInfo({
          name: info.name || undefined,
          street: info.street || undefined,
          houseNumber: info.house_number || undefined,
          postalCode: info.postal_code || undefined,
          city: info.city || undefined,
          logoBase64: info.logo_url || undefined,
        });
      }
    } catch (e) {
      // Ignore - company info is optional
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newFiles: UploadedFile[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.startsWith("image/") || file.type === "application/pdf") {
        newFiles.push({ file, preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined });
      }
    }

    setUploadedFiles(prev => [...prev, ...newFiles]);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set drag over to false if we're leaving the drop zone entirely
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const newFiles: UploadedFile[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.startsWith("image/") || file.type === "application/pdf") {
        newFiles.push({ file, preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined });
      }
    }

    if (newFiles.length > 0) {
      setUploadedFiles(prev => [...prev, ...newFiles]);
      toast.success(`${newFiles.length} Datei(en) hinzugefügt`);
    } else {
      toast.error("Bitte nur PDF oder Bilddateien hochladen");
    }
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => {
      const newFiles = [...prev];
      if (newFiles[index].preview) {
        URL.revokeObjectURL(newFiles[index].preview!);
      }
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const analyzeDocuments = async () => {
    if (uploadedFiles.length === 0) {
      toast.error("Bitte laden Sie mindestens ein Dokument hoch");
      return;
    }

    setAnalyzing(true);
    try {
      const formDataToSend = new FormData();
      uploadedFiles.forEach(({ file }) => {
        formDataToSend.append("files", file);
      });

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/parse-applicant-documents`,
        {
          method: "POST",
          body: formDataToSend,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 429) {
          toast.error("Zu viele Anfragen. Bitte versuchen Sie es später erneut.");
        } else if (response.status === 402) {
          toast.error("Dienst momentan nicht verfügbar.");
        } else {
          throw new Error(errorData.error || "Fehler bei der Dokumentenanalyse");
        }
        return;
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        const data = result.data;
        
        if (data.first_name) form.setValue("first_name", data.first_name);
        if (data.last_name) form.setValue("last_name", data.last_name);
        if (data.email) form.setValue("email", data.email);
        if (data.phone) form.setValue("phone", data.phone);
        if (data.address_street) form.setValue("address_street", data.address_street);
        if (data.address_zip) form.setValue("address_zip", data.address_zip);
        if (data.address_city) form.setValue("address_city", data.address_city);
        if (data.birth_date) form.setValue("birth_date", data.birth_date);
        if (data.desired_position) form.setValue("desired_position", data.desired_position);
        if (data.qualifications) form.setValue("qualifications", data.qualifications);
        if (data.drivers_license) {
          // Parse drivers_license string to array (e.g., "B, BE" -> ["B", "BE"])
          const classes = data.drivers_license.split(/[,\s]+/).filter((c: string) => c.trim());
          setSelectedLicenseClasses(classes);
        }

        toast.success("Dokumente erfolgreich analysiert! Bitte überprüfen Sie die Daten.");
      } else {
        toast.info("Keine Daten konnten extrahiert werden. Bitte füllen Sie das Formular manuell aus.");
      }
    } catch (error: any) {
      console.error("Error analyzing documents:", error);
      toast.error("Fehler bei der Dokumentenanalyse");
    } finally {
      setAnalyzing(false);
    }
  };

  const onSubmit = async (data: FormData) => {
    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("first_name", data.first_name);
      formDataToSend.append("last_name", data.last_name);
      formDataToSend.append("email", data.email);
      formDataToSend.append("phone", data.phone);
      if (data.address_street) formDataToSend.append("address_street", data.address_street);
      if (data.address_zip) formDataToSend.append("address_zip", data.address_zip);
      if (data.address_city) formDataToSend.append("address_city", data.address_city);
      if (data.birth_date) formDataToSend.append("birth_date", data.birth_date);
      if (data.desired_position) formDataToSend.append("desired_position", data.desired_position);
      if (data.qualifications) formDataToSend.append("qualifications", data.qualifications);
      if (selectedLicenseClasses.length > 0) {
        formDataToSend.append("drivers_license_classes", JSON.stringify(selectedLicenseClasses));
      }

      uploadedFiles.forEach(({ file }) => {
        formDataToSend.append("files", file, file.name);
      });

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-application`,
        {
          method: "POST",
          body: formDataToSend,
        }
      );

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          toast.error("Zu viele Bewerbungen. Bitte versuchen Sie es später erneut.");
          return;
        } else if (response.status === 409) {
          toast.error("Eine Bewerbung mit dieser E-Mail-Adresse existiert bereits.");
          return;
        } else {
          throw new Error(result.error || "Fehler beim Einreichen der Bewerbung");
        }
      }

      const applicantId = result.applicantId;

      try {
        await supabase.functions.invoke("notify-new-applicant", {
          body: {
            applicantId: applicantId,
            applicantName: `${data.first_name} ${data.last_name}`,
            applicantEmail: data.email,
            desiredPosition: data.desired_position || null,
          },
        });
      } catch (notifyError) {
        console.error("Error sending notification:", notifyError);
      }

      setSubmitted(true);
      toast.success("Bewerbung erfolgreich eingereicht!");
    } catch (error: any) {
      console.error("Error submitting application:", error);
      toast.error(error.message || "Fehler beim Einreichen der Bewerbung");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f1b2d]">
        <NetworkBackground />
        <Loader2 className="h-8 w-8 animate-spin text-yellow-400" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-[#0f1b2d]">
        <NetworkBackground />
        
        <div className="relative z-10 w-full max-w-md">
          <Card className="bg-[#162336]/90 backdrop-blur-xl border-yellow-500/20 shadow-2xl shadow-yellow-500/5">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-20 h-20 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center mb-4 shadow-lg shadow-yellow-500/30">
                <CheckCircle2 className="h-10 w-10 text-slate-900" />
              </div>
              <CardTitle className="text-2xl text-white">Vielen Dank!</CardTitle>
              <CardDescription className="text-slate-300 text-base">
                Ihre Bewerbung wurde erfolgreich eingereicht. Wir werden uns in Kürze bei Ihnen melden.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#0f1b2d]">
      <NetworkBackground />

      {/* Top-left Logo - static, no animation */}
      {(config?.logo_url || companyInfo?.logoBase64) && (
        <div className="fixed top-4 left-4 z-20 sm:top-6 sm:left-6">
          <img 
            src={config?.logo_url || companyInfo?.logoBase64} 
            alt="Firmenlogo" 
            className="h-12 sm:h-16 object-contain max-w-[180px]" 
          />
        </div>
      )}

      {/* Decorative elements */}
      <div className="fixed top-20 left-10 opacity-20">
        <Network className="h-24 w-24 text-yellow-400/60" />
      </div>
      <div className="fixed bottom-20 right-10 opacity-20">
        <Server className="h-20 w-20 text-yellow-400/60" />
      </div>
      <div className="fixed top-1/3 right-20 opacity-15">
        <Wifi className="h-16 w-16 text-yellow-300/50" />
      </div>
      <div className="fixed bottom-1/3 left-20 opacity-15">
        <Globe className="h-18 w-18 text-yellow-300/50" />
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4 py-16 pt-24 sm:pt-28">
        <div className="w-full max-w-2xl">
          {/* Hero Section */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent mb-3">
              {config?.title || "Karriere bei uns"}
            </h1>
            {config?.subtitle && (
              <p className="text-xl text-slate-300 font-light">
                {config.subtitle}
              </p>
            )}
          </div>

          {/* Main Form Card */}
          <Card className="bg-[#162336]/90 backdrop-blur-xl border-yellow-500/20 shadow-2xl shadow-yellow-500/5">
            <CardHeader className="text-center border-b border-yellow-500/10 pb-6">
              <CardTitle className="text-xl text-white flex items-center justify-center gap-2">
                <Sparkles className="h-5 w-5 text-yellow-400" />
                Bewerbungsformular
              </CardTitle>
              {config?.description && (
                <CardDescription className="text-slate-400 mt-2">
                  {config.description}
                </CardDescription>
              )}
            </CardHeader>
            
            <CardContent className="pt-6">
              {/* Document Upload Section with Drag & Drop */}
              <div 
                ref={dropZoneRef}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragOver(true);
                }}
                onClick={() => fileInputRef.current?.click()}
                className={`mb-6 p-5 rounded-xl border-2 border-dashed transition-all duration-300 cursor-pointer ${
                  isDragOver 
                    ? "bg-yellow-500/20 border-yellow-400 scale-[1.02]" 
                    : "bg-gradient-to-br from-yellow-500/5 to-amber-500/5 border-yellow-500/20 hover:bg-yellow-500/10 hover:border-yellow-400/40"
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                
                <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-yellow-400" />
                  Dokumente hochladen (optional)
                </h3>
                <p className="text-sm text-slate-400 mb-4">
                  {isDragOver 
                    ? "Dateien hier ablegen..." 
                    : "Dateien hierher ziehen oder klicken zum Auswählen. Wir können Ihre Daten automatisch ausfüllen."}
                </p>

                {uploadedFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4" onClick={(e) => e.stopPropagation()}>
                    {uploadedFiles.map((uploadedFile, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 px-3 py-2 bg-[#1a2b42] rounded-lg border border-yellow-500/20"
                      >
                        <FileText className="h-4 w-4 text-yellow-400" />
                        <span className="text-sm text-slate-300 truncate max-w-[150px]">{uploadedFile.file.name}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile(index);
                          }}
                          className="text-slate-500 hover:text-red-400 transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                    className="bg-[#1a2b42] border-yellow-500/30 text-slate-300 hover:bg-[#1f3350] hover:text-white hover:border-yellow-400/50"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Dateien auswählen
                  </Button>
                  {uploadedFiles.length > 0 && (
                    <Button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        analyzeDocuments();
                      }}
                      disabled={analyzing}
                      className="bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-slate-900 font-semibold border-0"
                    >
                      {analyzing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Analysiere...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Automatisch ausfüllen
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="first_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-300">Vorname *</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Max" 
                              {...field} 
                              className="bg-[#1a2b42] border-yellow-500/20 text-white placeholder:text-slate-500 focus:border-yellow-400 focus:ring-yellow-400/20"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="last_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-300">Nachname *</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Mustermann" 
                              {...field} 
                              className="bg-[#1a2b42] border-yellow-500/20 text-white placeholder:text-slate-500 focus:border-yellow-400 focus:ring-yellow-400/20"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-300">E-Mail *</FormLabel>
                          <FormControl>
                            <Input 
                              type="email" 
                              placeholder="max.mustermann@example.de" 
                              {...field} 
                              className="bg-[#1a2b42] border-yellow-500/20 text-white placeholder:text-slate-500 focus:border-yellow-400 focus:ring-yellow-400/20"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-300">Telefon *</FormLabel>
                          <FormControl>
                            <Input 
                              type="tel" 
                              placeholder="+49 123 456789" 
                              {...field} 
                              className="bg-[#1a2b42] border-yellow-500/20 text-white placeholder:text-slate-500 focus:border-yellow-400 focus:ring-yellow-400/20"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="address_street"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-300">Straße und Hausnummer</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Musterstraße 1" 
                            {...field} 
                            className="bg-[#1a2b42] border-yellow-500/20 text-white placeholder:text-slate-500 focus:border-yellow-400 focus:ring-yellow-400/20"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="address_zip"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-300">PLZ</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="12345" 
                              {...field} 
                              className="bg-[#1a2b42] border-yellow-500/20 text-white placeholder:text-slate-500 focus:border-yellow-400 focus:ring-yellow-400/20"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="address_city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-300">Stadt</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Musterstadt" 
                              {...field} 
                              className="bg-[#1a2b42] border-yellow-500/20 text-white placeholder:text-slate-500 focus:border-yellow-400 focus:ring-yellow-400/20"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="birth_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-300">Geburtsdatum</FormLabel>
                          <FormControl>
                            <Input 
                              type="date" 
                              {...field} 
                              className="bg-[#1a2b42] border-yellow-500/20 text-white placeholder:text-slate-500 focus:border-yellow-400 focus:ring-yellow-400/20"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormItem>
                      <FormLabel className="text-slate-300">Führerscheinklassen</FormLabel>
                      <Popover open={licensePopoverOpen} onOpenChange={setLicensePopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full justify-between bg-[#1a2b42] border-yellow-500/20 text-white hover:bg-[#1f3350] hover:text-white hover:border-yellow-400/40"
                          >
                            {selectedLicenseClasses.length > 0 
                              ? selectedLicenseClasses.join(", ")
                              : "Führerscheinklassen auswählen..."}
                            <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent 
                          className="w-[280px] p-3 bg-[#162336] border-yellow-500/20"
                          align="start"
                        >
                          <div className="grid grid-cols-3 gap-2">
                            {DRIVER_LICENSE_CLASSES.map((license) => (
                              <label
                                key={license.value}
                                className="flex items-center gap-2 cursor-pointer"
                              >
                                <Checkbox
                                  checked={selectedLicenseClasses.includes(license.value)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setSelectedLicenseClasses([...selectedLicenseClasses, license.value]);
                                    } else {
                                      setSelectedLicenseClasses(selectedLicenseClasses.filter(c => c !== license.value));
                                    }
                                  }}
                                  className="border-yellow-500/30 data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500"
                                />
                                <span className="text-sm text-slate-300">{license.label}</span>
                              </label>
                            ))}
                          </div>
                          {selectedLicenseClasses.length > 0 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedLicenseClasses([])}
                              className="w-full mt-3 text-slate-400 hover:text-white"
                            >
                              Auswahl zurücksetzen
                            </Button>
                          )}
                        </PopoverContent>
                      </Popover>
                    </FormItem>
                  </div>

                  {jobPositions.length > 0 && (
                    <FormField
                      control={form.control}
                      name="desired_position"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-300 font-medium">Gewünschte Stelle *</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="bg-[#1a2b42] border-2 border-yellow-500/30 text-white hover:border-yellow-400 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-500/20 transition-all">
                                <SelectValue placeholder="Bitte wählen Sie eine Stelle..." className="text-slate-400" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="bg-[#162336] border-2 border-yellow-500/30 shadow-xl shadow-yellow-500/5">
                              {jobPositions.map((position) => (
                                <SelectItem 
                                  key={position.id} 
                                  value={position.title}
                                  className="text-white font-medium hover:bg-yellow-600 focus:bg-yellow-600 focus:text-slate-900 cursor-pointer py-3 px-4 transition-colors"
                                >
                                  {position.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name="qualifications"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-300">Qualifikationen & Erfahrung</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Beschreiben Sie Ihre Qualifikationen und Berufserfahrung..."
                            rows={3}
                            {...field}
                            className="bg-[#1a2b42] border-yellow-500/20 text-white placeholder:text-slate-500 focus:border-yellow-400 focus:ring-yellow-400/20 resize-none"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-yellow-400 via-yellow-500 to-amber-500 hover:from-yellow-500 hover:via-yellow-600 hover:to-amber-600 text-slate-900 border-0 shadow-lg shadow-yellow-500/25 transition-all duration-300 hover:shadow-yellow-500/40"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Wird gesendet...
                      </>
                    ) : (
                      "Jetzt bewerben"
                    )}
                  </Button>
                </form>
              </Form>

              {config?.show_company_info && companyInfo?.name && (
                <div className="mt-6 pt-6 border-t border-yellow-500/10 text-center text-sm text-slate-400">
                  <Building2 className="h-4 w-4 inline-block mr-2 text-yellow-400" />
                  {companyInfo.name}
                  {companyInfo.city && `, ${companyInfo.city}`}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="mt-6 text-center text-sm text-slate-500">
            <p>© {new Date().getFullYear()} {companyInfo?.name || "Unternehmen"} • Alle Rechte vorbehalten</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplyPage;
